import './auth/startSessionPrewarm';
import './i18n';
import './index.css';
import App from './App';
import { StrictMode } from 'react';
import { bootstrap, setupFilePreview } from '@gokrew/ui';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { PermissionsProvider } from './context/PermissionContext';
import { AppConfigProvider, DialogProvider } from '@gokrew/ui/context';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { ErrorFallbackDialog } from './components/ui/ErrorFallbackDialog';
import { preloadCurrentRoute } from './utility/routePreloader';
import { installLiveDemoDriver } from './live-demo/liveDemoDriver';

installLiveDemoDriver();

const MAX_RETRIES = 3;

setupFilePreview(
  new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retries up to MAX_RETRIES times with React Query's default exponential backoff.
      // Only network/5xx errors are thrown to the error boundary (via throwOnError).
      // POST/mutation retries are not affected — this config applies to queries only.
      retry: MAX_RETRIES,
      refetchOnWindowFocus: true,
      throwOnError: (error: unknown, query) => {
        const status = (error as { response?: { status: number } })?.response
          ?.status;
        const shouldThrow = !status || status >= 500;
        if (
          shouldThrow &&
          query.state.fetchFailureCount >= MAX_RETRIES &&
          error &&
          typeof error === 'object'
        ) {
          Object.assign(error, { _isMaxRetry: true });
        }
        return shouldThrow;
      },
    },
  },
});

const app = (
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ resetError, error }) => (
        <ErrorFallbackDialog
          error={error}
          resetError={() => {
            queryClient.clear();
            resetError();
          }}
        />
      )}
    >
      <QueryClientProvider client={queryClient}>
        <AppConfigProvider>
          <DialogProvider>
            <AuthProvider>
              <PermissionsProvider>
                <App />
                <Toaster
                  position="top-right"
                  reverseOrder={false}
                  // Offsets toasts below the header so they don't overlap
                  // the user-info area in its top-right corner.
                  containerStyle={{
                    top: 'calc(var(--topbar-height) + 1rem)',
                  }}
                />
              </PermissionsProvider>
            </AuthProvider>
          </DialogProvider>
        </AppConfigProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);

preloadCurrentRoute();
bootstrap('root', app);
